import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { KitSelection } from '@/types/kit';

/*
 * MIGRATION GUIDE - Kit-Based Selection Architecture
 * =================================================
 * 
 * This file contains the AppContext which has been migrated from individual product selection 
 * to a kit-based selection approach. Below is guidance for developers working with this code:
 * 
 * DEPRECATED LEGACY APPROACH:
 * - equipoSeleccionado: Equipo | null
 * - mascarillaSeleccionada: Mascarilla | null
 * - Individual product hooks (useProducts, useEquipos, useMascarillas)
 * 
 * NEW KIT-BASED APPROACH:
 * - kitSeleccionado: KitSelection | null
 * - useKitData hook for fetching available kits
 * - KitSelectionCard component for kit display
 * - Unified pricing and component management
 * 
 * MIGRATION STEPS FOR FUTURE CLEANUP:
 * 1. Ensure no active usage of deprecated properties in components
 * 2. Verify all quotes use kit-based data or have compatibility layers
 * 3. Remove deprecated properties from EleccionProgress interface
 * 4. Remove deprecated type interfaces (Equipo, Mascarilla)
 * 5. Remove deprecated hooks (useProducts, useEquipos, useMascarillas)
 * 6. Remove compatibility methods from useAppContext hook
 * 7. Update quote service to only handle kit-based selections
 * 
 * BACKWARD COMPATIBILITY:
 * - Legacy quotes will still load correctly through compatibility layers
 * - getActiveSelection() method handles both individual and kit selections
 * - hasSelection() method works with both approaches
 * - Quote services convert individual selections to kit format when needed
 * 
 * For new features, always use:
 * - KitSelection type instead of individual Equipo/Mascarilla
 * - useKitData hook instead of useProducts
 * - selectKit() instead of updateEleccionProgress with individual products
 */

interface MaskSizing {
  maskSize: 'S' | 'M' | 'L';
  measurementMm: number;
  measureMethod: 'regla' | 'manual';
  borderline: boolean;
}

/**
 * @deprecated Use KitSelection instead. Individual equipment selection is being phased out in favor of kit-based selection.
 * This interface is maintained for backward compatibility with existing quotes only.
 * Will be removed in a future version.
 * @see KitSelection for the new kit-based approach
 */
interface Equipo {
  marca: 'ResMed' | 'Yuwell';
  modelo: string;
  precio: number;
  conectividad: string;
  garantia: string;
  caracteristicas: string[];
  imagen: string;
}

/**
 * @deprecated Use KitSelection instead. Individual mask selection is being phased out in favor of kit-based selection.
 * This interface is maintained for backward compatibility with existing quotes only.
 * Will be removed in a future version.
 * @see KitSelection for the new kit-based approach
 */
interface Mascarilla {
  marca: 'ResMed' | 'Yuwell';
  modelo: string;
  precio: number;
  tipo: 'Nasal' | 'Oronasal';
  caracteristicas: string[];
  compatibilidad: string[];
  imagen: string;
}

interface EleccionProgress {
  breathingSelfReport: string;
  noseAnswers: Record<string, number>;
  noseScore: number | null;
  maskRecommendation: string;
  /**
   * @deprecated Use `kitSeleccionado` instead. Individual product selection is being phased out in favor of kit-based selection.
   * This property is maintained for backward compatibility with existing quotes only.
   * Will be removed in a future version.
   * @see kitSeleccionado for the new kit-based approach
   */
  equipoSeleccionado: Equipo | null;
  /**
   * @deprecated Use `kitSeleccionado` instead. Individual product selection is being phased out in favor of kit-based selection.
   * This property is maintained for backward compatibility with existing quotes only.
   * Will be removed in a future version.
   * @see kitSeleccionado for the new kit-based approach
   */
  mascarillaSeleccionada: Mascarilla | null;
  // Kit-based selection (new approach)
  kitSeleccionado: KitSelection | null;
  metodoPago: string;
  bunoIncluido: boolean;
  maskSizing: MaskSizing | null;
  currentStep: string;
  completedSteps: string[];
}

interface AppState {
  dispositivo: {
    marca: 'ResMed' | 'Yuwell' | null;
  };
  eleccion: EleccionProgress;
}

interface AppContextType {
  state: AppState;
  updateDispositivo: (marca: 'ResMed' | 'Yuwell' | null) => void;
  updateEleccionProgress: (updates: Partial<EleccionProgress>) => void;
  resetEleccionProgress: () => void;
  // Kit-specific methods
  selectKit: (kitSelection: KitSelection) => void;
  clearKit: () => void;
  getSelectedKit: () => KitSelection | null;
  // Compatibility helpers
  hasSelection: () => boolean;
  getActiveSelection: () => { 
    type: 'individual' | 'kit' | null; 
    data: KitSelection | { equipo: Equipo; mascarilla: Mascarilla } | null;
  };
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const initialEleccionProgress: EleccionProgress = {
  breathingSelfReport: '',
  noseAnswers: {},
  noseScore: null,
  maskRecommendation: '',
  equipoSeleccionado: null,
  mascarillaSeleccionada: null,
  kitSeleccionado: null,
  metodoPago: '',
  bunoIncluido: true,
  maskSizing: null,
  currentStep: 'respiracion',
  completedSteps: []
};

const initialState: AppState = {
  dispositivo: {
    marca: null
  },
  eleccion: initialEleccionProgress
};

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(initialState);

  const updateDispositivo = (marca: 'ResMed' | 'Yuwell' | null) => {
    setState(prev => ({
      ...prev,
      dispositivo: { marca }
    }));
  };

  const updateEleccionProgress = (updates: Partial<EleccionProgress>) => {
    setState(prev => ({
      ...prev,
      eleccion: {
        ...prev.eleccion,
        ...updates
      }
    }));
  };

  const resetEleccionProgress = () => {
    setState(prev => ({
      ...prev,
      eleccion: initialEleccionProgress
    }));
  };

  // Kit-specific methods
  const selectKit = (kitSelection: KitSelection) => {
    setState(prev => ({
      ...prev,
      eleccion: {
        ...prev.eleccion,
        kitSeleccionado: kitSelection,
        // Clear individual selections when selecting a kit
        equipoSeleccionado: null,
        mascarillaSeleccionada: null
      }
    }));
  };

  const clearKit = () => {
    setState(prev => ({
      ...prev,
      eleccion: {
        ...prev.eleccion,
        kitSeleccionado: null
      }
    }));
  };

  const getSelectedKit = (): KitSelection | null => {
    return state.eleccion.kitSeleccionado;
  };

  // Compatibility helpers
  const hasSelection = (): boolean => {
    const { equipoSeleccionado, mascarillaSeleccionada, kitSeleccionado } = state.eleccion;
    return !!(kitSeleccionado || (equipoSeleccionado && mascarillaSeleccionada));
  };

  const getActiveSelection = (): { 
    type: 'individual' | 'kit' | null; 
    data: KitSelection | { equipo: Equipo; mascarilla: Mascarilla } | null;
  } => {
    const { equipoSeleccionado, mascarillaSeleccionada, kitSeleccionado } = state.eleccion;
    
    if (kitSeleccionado) {
      return {
        type: 'kit',
        data: kitSeleccionado
      };
    }
    
    if (equipoSeleccionado && mascarillaSeleccionada) {
      return {
        type: 'individual',
        data: { equipo: equipoSeleccionado, mascarilla: mascarillaSeleccionada }
      };
    }
    
    return { type: null, data: null };
  };

  return (
    <AppContext.Provider value={{
      state,
      updateDispositivo,
      updateEleccionProgress,
      resetEleccionProgress,
      selectKit,
      clearKit,
      getSelectedKit,
      hasSelection,
      getActiveSelection
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Export types for use in components
export type { MaskSizing, Equipo, Mascarilla, EleccionProgress, KitSelection };